/**
 * Configuration Socket.IO pour le backoffice
 */

import { io, Socket } from 'socket.io-client'

// Clé de stockage du token admin
const TOKEN_KEY = 'LS_COOP_FINANCE_BO_ACCESS_TOKEN'

// Configuration depuis les variables d'environnement
const getEnvVar = (key: string, defaultValue: string): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (process.env as any)[key] || defaultValue
}

// URL de base du serveur Socket.IO
const getSocketBaseUrl = (): string => {
  const socketUrl = getEnvVar('VUE_APP_SOCKET_URL', '')
  if (socketUrl) {
    try {
      const url = new URL(socketUrl)
      return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`
    } catch {
      return socketUrl.replace(/\/socket\.io.*$/i, '').replace(/\/$/, '')
    }
  }
  
  const apiUrl = getEnvVar('VUE_APP_API_URL', 'https://backend.coop-finance-app.wixoon.dev')
  const baseUrl = apiUrl.replace(/\/api$/, '')
  const socketPort = getEnvVar('VUE_APP_SOCKET_PORT', '3000')
  
  // En développement local, utiliser le port directement
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    return `http://localhost:${socketPort}`
  }
  
  // En production, utiliser l'URL du domaine principal (sans port)
  // Nginx proxifiera /socket.io/ vers le serveur Socket.IO local
  try {
    const url = new URL(baseUrl)
    // Ne pas ajouter le port en production - laisser Nginx gérer le routage
    return `${url.protocol}//${url.hostname}`
  } catch (error) {
    console.warn('[Socket.IO BO] URL invalide, utilisation de localhost:', error)
    return `http://localhost:${socketPort}`
  }
}

const socketBaseUrl = getSocketBaseUrl()
let socket: Socket | null = null

/**
 * Obtenir le token admin
 */
const getAdminToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * Initialiser la connexion Socket.IO pour le backoffice
 */
export const initSocket = (): Socket | null => {
  if (socket?.connected) {
    return socket
  }

  try {
    const token = getAdminToken()
    const socketPath = getEnvVar('VUE_APP_SOCKET_PATH', '/socket.io')
    
    console.log('[Socket.IO BO] Connexion à:', socketBaseUrl, 'avec path:', socketPath)
    
    socket = io(socketBaseUrl, {
      path: socketPath,
      auth: {
        token: token || ''
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000
    })

    socket.on('connect', () => {
      console.log('[Socket.IO BO] Connexion établie')
      // Authentifier et rejoindre le room admin
      if (token) {
        socket?.emit('authenticate', { token })
      }
      // Rejoindre le room backoffice admin
      socket?.emit('admin.connect')
    })

    socket.on('admin.connected', (data) => {
      console.log('[Socket.IO BO] Connecté au room admin:', data.room)
    })

    socket.on('disconnect', (reason) => {
      console.warn('[Socket.IO BO] Déconnexion:', reason)
    })

    socket.on('connect_error', (error) => {
      console.error('[Socket.IO BO] Erreur de connexion:', error.message)
    })

    socket.on('reconnect', (attemptNumber) => {
      console.log('[Socket.IO BO] Reconnexion réussie après', attemptNumber, 'tentative(s)')
      const currentToken = getAdminToken()
      if (currentToken) {
        socket?.emit('authenticate', { token: currentToken })
      }
      socket?.emit('admin.connect')
    })

    return socket
  } catch (error) {
    console.warn('[Socket.IO BO] Impossible d\'initialiser Socket.IO:', error)
    return null
  }
}

/**
 * Obtenir l'instance Socket.IO
 */
export const getSocket = (): Socket | null => {
  if (!socket || !socket.connected) {
    return initSocket()
  }
  return socket
}

/**
 * Déconnecter le socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.emit('admin.disconnect')
    socket.disconnect()
    socket = null
  }
}

/**
 * Vérifier si Socket.IO est connecté
 */
export const isSocketConnected = (): boolean => {
  return socket?.connected || false
}

/**
 * Écouter un événement
 */
export const onEvent = (eventName: string, callback: (data: any) => void): void => {
  const currentSocket = getSocket()
  if (currentSocket) {
    currentSocket.on(eventName, callback)
    console.log(`[Socket.IO BO] Écoute de l'événement: ${eventName}`)
  }
}

/**
 * Arrêter d'écouter un événement
 */
export const offEvent = (eventName: string, callback?: (data: any) => void): void => {
  if (socket) {
    if (callback) {
      socket.off(eventName, callback)
    } else {
      socket.off(eventName)
    }
  }
}

/**
 * Rejoindre un room
 */
export const joinRoom = (roomName: string): void => {
  const currentSocket = getSocket()
  if (currentSocket) {
    currentSocket.emit('join', roomName)
    console.log('[Socket.IO BO] Rejoint le room:', roomName)
  }
}

/**
 * Quitter un room
 */
export const leaveRoom = (roomName: string): void => {
  if (socket) {
    socket.emit('leave', roomName)
    console.log('[Socket.IO BO] Quitté le room:', roomName)
  }
}

export default socket
